import {useMemo} from 'react';
import {addDays, differenceInDays, endOfISOWeek, isAfter, startOfDay, startOfISOWeek} from 'date-fns';

import {getAppLanguage} from '../../../data';
import {useAppSelector} from '../../../hooks';
import {TranslationService} from '../../../@types/i18next';
import {knownLocales} from '@lib/utils';
import {Workday} from '../../../data/timeslots/apiTypes';
import {getAllWorkdays, getSomeWorkdaysHaveLoaded} from '../../../data/timeslots/selectors/index';
import {dateKeyForDateTimeString} from '../../../data/timeslots/dateTimeHelpers';

type Workdays = {
  officeDays: Workday[];
  otherDays: Workday[];
};

type ReturnType = {
  status: 'OfficeWeek' | 'NonOfficeWeek' | 'NotPlanned';
  isLoading: boolean;
  title: string;
  subtitle: string;
};

// Day from which to determine if it's late in the week, and to start looking at next week's workdays
const END_OF_WEEK_THRESHOLD = 4; // Thursday

export const useHomeUpcomingDays_timeslots = (initialReferenceDate: Date, t: TranslationService): ReturnType => {
  const language = useAppSelector(getAppLanguage);
  const isLoading = useAppSelector((state) => !getSomeWorkdaysHaveLoaded(state));
  const allWorkdays = useAppSelector(getAllWorkdays);
  const today = useMemo(() => new Date(), []);

  const upcomingWorkdays = useMemo(
    () => allWorkdays.filter((workday) => isAfter(new Date(workday.localStart), today)),
    [allWorkdays, today],
  );

  // Decide to use current or next week
  const adjustedReferenceDate = useMemo(() => {
    const endOfCurrentWeek = endOfISOWeek(today);

    const hasRemainingOfficeDaysThisWeek = upcomingWorkdays.some((workday) => {
      const date = new Date(workday.localStart);

      const isAfterToday = isAfter(date, today);
      const isBeforeEndOfWeek = !isAfter(date, endOfCurrentWeek);

      return isAfterToday && isBeforeEndOfWeek && workday.status === 'OfficeDay';
    });

    const isLateInWeek = today.getDay() >= END_OF_WEEK_THRESHOLD;

    // If it’s late in the week and there’s no office days left, shift to next week
    if (!hasRemainingOfficeDaysThisWeek && isLateInWeek) {
      const nextMonday = addDays(startOfISOWeek(today), 7);

      return nextMonday;
    }

    return initialReferenceDate;
  }, [upcomingWorkdays, initialReferenceDate, today]);

  const isRefDateToday = !differenceInDays(adjustedReferenceDate, today);

  // Find upcoming workdays for the chosen week
  const {officeDays, otherDays} = useMemo(() => {
    const endOfWeek = endOfISOWeek(adjustedReferenceDate);

    return upcomingWorkdays.reduce(
      (result, workday) => {
        const date = new Date(workday.localStart);

        const isBeforeStartOfWeek = date < startOfDay(adjustedReferenceDate);
        if (isBeforeStartOfWeek) return result;

        if (isAfter(date, endOfWeek)) return result;

        if (workday.status === 'OfficeDay') {
          return {
            ...result,
            officeDays: [...result.officeDays, workday],
          };
        }

        return {
          ...result,
          otherDays: [...result.otherDays, workday],
        };
      },
      {officeDays: [], otherDays: []} as Workdays,
    );
  }, [upcomingWorkdays, adjustedReferenceDate]);

  const status: ReturnType['status'] =
    officeDays.length > 0 ? 'OfficeWeek' : otherDays.length > 0 ? 'NonOfficeWeek' : 'NotPlanned';

  const title = isRefDateToday ? t('workplace:UpcomingDays') : t('workplace:UpcomingDaysNextWeek');

  const subtitle = useMemo(() => {
    switch (status) {
      case 'NonOfficeWeek':
        return t('workplace:NoOfficeDays');

      case 'NotPlanned':
        return t('workplace:WeekNotPlanned');

      case 'OfficeWeek': {
        const locale = knownLocales[language];
        const dateStrings = officeDays.map((day) => dateKeyForDateTimeString(day.localStart));
        const uniqueDatesWithOfficeDay = Array.from(new Set(dateStrings));
        const weekdaysLabel = resolveWeekdaysLabel(uniqueDatesWithOfficeDay, locale, t);

        return weekdaysLabel;
      }
    }
  }, [language, officeDays, status, t]);

  return useMemo(
    () => ({
      status,
      isLoading,
      title,
      subtitle,
    }),
    [isLoading, status, subtitle, title],
  );
};

const getDayNames = (dateStrings: string[], locale: Locale): string[] =>
  dateStrings.map((dateStr) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(locale.code, {weekday: 'long'}).format(date);
  });

export const resolveWeekdaysLabel = (dateStrings: string[], locale: Locale, t: TranslationService) => {
  const dates = dateStrings.map((str) => new Date(str));
  const weekdayNumbers = new Set(dates.map((d) => d.getDay())); // Note: `getDay()` returns 0-6 (Sunday-Saturday)

  const isFullWorkweek = [1, 2, 3, 4, 5].every((d) => weekdayNumbers.has(d));

  if (isFullWorkweek) {
    return `${t('translation:Monday')} ${t('translation:ToTagForTimeInterval')} ${t('translation:Friday')}`;
  }

  const weekdayNames = getDayNames(dateStrings, locale);

  return weekdayNames.join(', ');
};
